import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pill, ChevronLeft, AlertCircle, MailCheck } from 'lucide-react';
import { useForgotPassword } from '@/api/auth';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const forgotMut = useForgotPassword();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await forgotMut.mutateAsync({ email });
    } catch {
      // Erreur affichée via forgotMut.error
    }
  }

  // Le serveur répond la même chose que l'adresse existe ou non — c'est
  // volontaire (voir la route). L'écran doit donc dire la même chose aussi,
  // sinon la précaution côté serveur ne sert à rien.
  const sent = forgotMut.isSuccess;

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
            <Pill size={19} color="#fff" strokeWidth={2.25} />
          </div>
          <div>
            <div className="text-[17px] font-semibold text-slate-900">Officina</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">RH Pharmacie</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          {sent ? (
            <>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                <MailCheck size={21} strokeWidth={2} />
              </div>
              <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">
                Vérifiez votre boîte mail
              </h2>
              <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
                Si un compte existe pour <strong className="text-slate-700">{email}</strong>, un lien
                de réinitialisation vient d'être envoyé. Il est valable une heure et ne peut servir
                qu'une fois.
              </p>
              <p className="text-[12px] text-slate-400 mt-4 leading-relaxed">
                Rien reçu au bout de quelques minutes ? Regardez dans les indésirables, et vérifiez
                l'adresse saisie.
              </p>
              <Link
                to="/login"
                className="mt-6 w-full inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors"
              >
                Revenir à la connexion
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-[24px] font-semibold tracking-tight text-slate-900">
                Mot de passe oublié
              </h2>
              <p className="text-[13px] text-slate-500 mt-1">
                Saisissez votre adresse : nous vous enverrons un lien pour en choisir un nouveau.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    autoFocus
                    required
                  />
                </div>

                {forgotMut.error && (
                  <div className="bg-danger-bg border border-danger/30 rounded-lg p-2.5 flex items-start gap-2 text-[11.5px] text-danger">
                    <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                    <span>
                      {(forgotMut.error as Error).message || 'Envoi impossible pour le moment'}
                    </span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={forgotMut.isPending}
                  className="w-full bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {forgotMut.isPending ? 'Envoi…' : 'Envoyer le lien'}
                </button>
              </form>

              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-800"
              >
                <ChevronLeft size={14} />
                Revenir à la connexion
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
